import type { GetUserProfileResponse } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import { ShowRoomPage } from '../shows/index.js';
import { MemberProfilePage } from './MemberProfilePage.js';
import './MemberProfilePage.css';

type ResolveState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: GetUserProfileResponse };

/**
 * Canonical `/@username` route. Resolves any user, then renders the model
 * storefront/live room for creators or the community profile for members.
 */
export function ProfilePage(): ReactElement {
  const { username: raw } = useParams<{ username: string }>();
  const username = (raw ?? '').replace(/^@/, '');
  const { api } = useAuth();
  const [state, setState] = useState<ResolveState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    void api
      .getUserProfile(username)
      .then((data) => {
        if (active) setState({ status: 'ready', data });
      })
      .catch(() => {
        if (active) setState({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [api, username]);

  if (state.status === 'loading') {
    return <p className="member-profile__loading">{t('common.profile.loading')}</p>;
  }

  if (state.status === 'error') {
    return (
      <section className="member-profile member-profile--missing">
        <h1>{t('common.profile.notFoundTitle')}</h1>
        <p>{t('common.profile.notFoundBody')}</p>
      </section>
    );
  }

  if (state.data.profile.role === 'MODEL') {
    return <ShowRoomPage />;
  }

  return <MemberProfilePage key={username} username={username} initial={state.data} />;
}
